import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CalendarRange, Facebook, Loader2, Mail, MapPin, Phone, PhoneCall, Send, Users } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SlidePuzzleCaptcha from '../components/SlidePuzzleCaptcha';
import { setSeoMetadata } from '../lib/seo';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { useHeroMedia } from '../hooks/useHeroMedia';
import { ROUTES, CONTACT_PAGE } from '../data/stjSite';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Exactly the choices on the legacy form. */
const INTERESTS = [
  'Lodging/Villa/Private Home Rental',
  'Private Boat',
  'Shared Boat',
  'BVI',
  'LimeOut',
  'Private Transportation',
  'Ferry Tickets',
  'Activities ie: Snorkeling, Fishing Charters, Night Kayaking',
];
const PARTY_SIZES = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11+'];
const CONSENT = 'By providing your email and phone number, you agree to our Privacy Policy and terms of use '
  + 'to receive prerecorded and/or autodialed marketing calls or text messages from Reserve St John, '
  + 'including emails and calls regarding your free quote. Your consent to receive automated marketing '
  + 'calls is not a condition of purchase. Message and data rates may apply.';

const EMPTY = {
  name: '', email: '', phone: '', party_size: '', adults: '', children: '',
  interests: [], travel_dates: '', message: '', lastName: '',
};

/**
 * /book-your-trip-to-st-john-vi - "Planning Your Trip", the page the home page's
 * "Get Your FREE Villa Rental Quote" button opens.
 *
 * Field for field the legacy form, with the slide puzzle in place of reCAPTCHA. It posts to the
 * same pipeline as the contact page: puzzle -> CRM opportunity (with the trip details) -> email to
 * the office.
 */
const BookYourTripPage = () => {
  const { supportEmail } = useSiteSettings();
  const hero = useHeroMedia();
  const [form, setForm] = useState(EMPTY);
  const [captcha, setCaptcha] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const email = supportEmail || 'info@reservestj.com';

  useEffect(() => {
    const description = 'Tell us about your St John trip and we will plan it with you - villas, boat '
      + 'charters, BVI days, transport and ferry tickets. Free concierge service, no booking fees.';
    setSeoMetadata({
      title: 'Planning Your Trip',
      description,
      keywords: 'plan a trip to st john, st john villa quote, st john concierge, book your trip st john usvi',
      canonicalPath: ROUTES.bookYourTrip,
      ogType: 'website',
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: 'Planning Your Trip - St John, Virgin Islands',
        description,
      },
    });
  }, []);

  const update = (field) => (event) => setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const toggleInterest = (value) => setForm((prev) => ({
    ...prev,
    interests: prev.interests.includes(value)
      ? prev.interests.filter((item) => item !== value)
      : [...prev.interests, value],
  }));

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    if (form.lastName) { setSent(true); return; }
    if (!form.interests.length) {
      setError('Please tick at least one trip need or interest.');
      return;
    }
    if (!captcha) {
      setError('Please slide the puzzle piece into the gap so we know you are human.');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/contact/submit`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        reason: 'Trip planning / free villa quote',
        message: form.message,
        party_size: form.party_size,
        adults: form.adults,
        children: form.children,
        interests: form.interests,
        travel_dates: form.travel_dates,
        captcha_token: captcha.token,
        captcha_x: captcha.x,
        honeypot: form.lastName,
        page: ROUTES.bookYourTrip,
      });
      setSent(true);
      setForm(EMPTY);
      setCaptcha(null);
    } catch (submitError) {
      const detail = submitError.response?.data?.detail;
      setError(typeof detail === 'string' ? detail
        : 'Sorry, that did not go through. Please call or text (340) 513-7080 and we will help right away.');
      setCaptcha(null);
    } finally {
      setSending(false);
    }
  };

  const inputClass = 'w-full rounded border border-slate-300 bg-white px-4 py-3 text-slate-900 placeholder-slate-400 outline-none focus:border-blue-600';

  return (
    <div className="min-h-screen bg-white" data-testid="book-your-trip-page">
      <Header />

      {/* ================= HERO ================= */}
      <section className="relative flex min-h-[46vh] items-center justify-center overflow-hidden lg:min-h-[54vh]" data-testid="trip-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-sky-100 via-white to-white" />
        {hero.videoUrl ? (
          <video key={hero.videoUrl} autoPlay muted loop playsInline preload="metadata"
            poster={hero.backgroundImageUrl || undefined} className="absolute inset-0 h-full w-full object-cover">
            <source src={hero.videoUrl} type={hero.videoMimeType} />
          </video>
        ) : hero.backgroundImageUrl ? (
          <div className="absolute inset-0 h-full w-full bg-cover bg-center" style={{ backgroundImage: `url(${hero.backgroundImageUrl})` }} />
        ) : null}
        <div className="absolute inset-0 bg-black/25" />
        <div className="relative z-10 w-full px-4 py-24 text-center">
          <h1 className="text-4xl font-extrabold italic tracking-tight text-white drop-shadow-[0_3px_10px_rgba(0,0,0,0.75)] sm:text-5xl lg:text-6xl">
            Planning Your Trip
          </h1>
          <h2 className="mt-4 text-xl font-semibold text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.75)] sm:text-2xl">
            St John, Virgin Islands
          </h2>
        </div>
      </section>

      <main className="mx-auto max-w-7xl px-4 py-12 lg:py-16">
        <h2 className="text-center text-3xl font-extrabold uppercase italic tracking-tight text-slate-900 sm:text-4xl" data-testid="trip-tagline">
          We Give You Local Knowledge For Your Best Stay
        </h2>

        <div className="mt-10 grid gap-10 lg:grid-cols-[1.5fr,1fr]">
          {/* ---------------- form ---------------- */}
          <section>
            {sent ? (
              <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-6" data-testid="trip-form-success">
                <p className="text-lg font-bold text-emerald-800">Thank you &mdash; we have your trip details.</p>
                <p className="mt-2 text-emerald-900/80">
                  We will come back to you with options. If it is urgent, call or text{' '}
                  <a href="tel:13405137080" className="font-semibold underline">(340) 513-7080</a>.
                </p>
                <button type="button" onClick={() => setSent(false)}
                  className="mt-4 rounded border-2 border-emerald-600 px-5 py-2 text-sm font-bold uppercase tracking-wide text-emerald-700 hover:bg-emerald-100"
                  data-testid="trip-form-send-another">
                  Send another request
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-8" data-testid="trip-form">
                {/* ---- your trip information ---- */}
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-slate-900" data-testid="trip-info-title">Your Trip Information</h3>

                  <div>
                    <label htmlFor="trip-name" className="mb-1.5 block text-sm font-semibold text-slate-700">Enter your name *</label>
                    <input id="trip-name" required value={form.name} onChange={update('name')} placeholder="Enter your name" className={inputClass} data-testid="trip-name" />
                  </div>
                  <div>
                    <label htmlFor="trip-email" className="mb-1.5 block text-sm font-semibold text-slate-700">Enter your email address *</label>
                    <input id="trip-email" type="email" required value={form.email} onChange={update('email')} placeholder="Enter your email address" className={inputClass} data-testid="trip-email" />
                  </div>
                  <div>
                    <label htmlFor="trip-phone" className="mb-1.5 block text-sm font-semibold text-slate-700">Phone *</label>
                    <input id="trip-phone" required value={form.phone} onChange={update('phone')} className={inputClass} data-testid="trip-phone" />
                  </div>
                </div>

                {/* ---- your travel information ---- */}
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-slate-900" data-testid="trip-travel-title">Your Travel Information</h3>

                  <div className="grid gap-4 sm:grid-cols-3">
                    <div>
                      <label htmlFor="trip-party" className="mb-1.5 block text-sm font-semibold text-slate-700">Members In Party *</label>
                      <select id="trip-party" required value={form.party_size} onChange={update('party_size')} className={inputClass} data-testid="trip-party-size">
                        <option value="">Select</option>
                        {PARTY_SIZES.map((size) => <option key={size} value={size}>{size}</option>)}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="trip-adults" className="mb-1.5 block text-sm font-semibold text-slate-700">Adults *</label>
                      <input id="trip-adults" required value={form.adults} onChange={update('adults')} placeholder="Number of Adults" className={inputClass} data-testid="trip-adults" />
                    </div>
                    <div>
                      <label htmlFor="trip-children" className="mb-1.5 block text-sm font-semibold text-slate-700">Children *</label>
                      <input id="trip-children" required value={form.children} onChange={update('children')} placeholder="Number of Children 12 & Under" className={inputClass} data-testid="trip-children" />
                    </div>
                  </div>

                  <fieldset>
                    <legend className="mb-2 text-sm font-semibold text-slate-700">Trip Needs/Interests *</legend>
                    <div className="grid gap-2 sm:grid-cols-2" data-testid="trip-interests">
                      {INTERESTS.map((interest) => (
                        <label key={interest} className="flex cursor-pointer items-start gap-2 rounded border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:border-blue-300">
                          <input
                            type="checkbox"
                            className="mt-0.5"
                            checked={form.interests.includes(interest)}
                            onChange={() => toggleInterest(interest)}
                            data-testid={`trip-interest-${interest.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                          />
                          {interest}
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  <div>
                    <label htmlFor="trip-dates" className="mb-1.5 block text-sm font-semibold text-slate-700">Estimated Arrival/Departure Date *</label>
                    <input id="trip-dates" required value={form.travel_dates} onChange={update('travel_dates')} className={inputClass} data-testid="trip-dates" />
                    <p className="mt-1 text-xs text-slate-500">Please choose 2 dates, your estimated arrival date and your departure date</p>
                  </div>

                  <div>
                    <label htmlFor="trip-message" className="mb-1.5 block text-sm font-semibold text-slate-700">Info about Trip / Questions *</label>
                    <textarea id="trip-message" required rows={4} value={form.message} onChange={update('message')} className={inputClass} data-testid="trip-message" />
                  </div>

                  {/* anti-spam: hidden from people, irresistible to bots */}
                  <div className="hidden" aria-hidden="true">
                    <label htmlFor="trip-last-name">Last Name</label>
                    <input id="trip-last-name" tabIndex={-1} autoComplete="off" value={form.lastName} onChange={update('lastName')} />
                  </div>

                  <SlidePuzzleCaptcha onSolved={setCaptcha} onReset={() => setCaptcha(null)} />

                  {error && <p className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700" data-testid="trip-form-error">{error}</p>}

                  <button
                    type="submit"
                    disabled={sending || !captcha}
                    className="inline-flex items-center gap-2 rounded bg-red-600 px-8 py-4 font-bold uppercase tracking-wide text-white transition hover:bg-red-700 disabled:opacity-60"
                    data-testid="trip-submit"
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    {sending ? 'Sending...' : 'Submit'}
                  </button>

                  <p className="text-xs leading-relaxed text-slate-500" data-testid="trip-consent">{CONSENT}</p>
                  <p className="text-xs text-slate-500">
                    <Link to="/privacy-policy" className="underline hover:text-blue-700">Privacy Policy</Link>
                    {' \u00b7 '}
                    <Link to="/terms-conditions" className="underline hover:text-blue-700">Terms of use</Link>
                  </p>
                </div>
              </form>
            )}
          </section>

          {/* ---------------- contact details ---------------- */}
          <aside className="space-y-6" data-testid="trip-details">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
              <a href={`mailto:${email}`} className="flex items-start gap-3 text-slate-700 hover:text-blue-700" data-testid="trip-email-link">
                <Mail className="mt-0.5 h-5 w-5 shrink-0 text-blue-700" />
                <span className="font-semibold">{email}</span>
              </a>
              <p className="mt-4 flex items-start gap-3 text-slate-700" data-testid="trip-address">
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-blue-700" />
                <span>P.O. Box 1797 St John VI 00831</span>
              </p>
              <a href={CONTACT_PAGE.phones[0].tel} className="mt-4 flex items-start gap-3 text-slate-700 hover:text-blue-700" data-testid="trip-phone-help">
                <Phone className="mt-0.5 h-5 w-5 shrink-0 text-blue-700" />
                <span>
                  <span className="block font-bold">{CONTACT_PAGE.phones[0].number}</span>
                  <span className="text-sm text-slate-500">{CONTACT_PAGE.phones[0].label}</span>
                </span>
              </a>
              <a href={CONTACT_PAGE.facebookGroup} target="_blank" rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-2 text-slate-700 hover:text-blue-700" data-testid="trip-facebook">
                <Facebook className="h-5 w-5 text-blue-700" /> Facebook Group
              </a>
            </div>

            <a href={CONTACT_PAGE.phones[0].tel}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-blue-800 px-6 py-4 font-bold uppercase tracking-wide text-white transition hover:bg-blue-900"
              data-testid="trip-click-to-call">
              <PhoneCall className="h-5 w-5" /> Click to Call {CONTACT_PAGE.phones[0].number}
            </a>

            <div className="rounded-2xl border border-slate-200 p-6 text-sm text-slate-600">
              <p className="flex items-center gap-2 font-bold uppercase tracking-wide text-slate-900">
                <Users className="h-4 w-4 text-blue-700" /> Free concierge service
              </p>
              <p className="mt-2">
                Tell us the dates and the party and we will come back with villas, boats and activities that
                fit &mdash; there is no fee for our help.
              </p>
              <p className="mt-3 flex items-center gap-2 font-semibold text-slate-800">
                <CalendarRange className="h-4 w-4 text-blue-700" /> Prefer to talk it through?
              </p>
              <Link to="/book/kris-dougherty" className="mt-1 inline-block font-semibold text-blue-800 underline hover:text-blue-900" data-testid="trip-schedule-call">
                Schedule a complimentary call
              </Link>
            </div>
          </aside>
        </div>
      </main>

      {/* ================= NEWSLETTER ================= */}
      <section className="mx-auto max-w-4xl px-4 pb-14 text-center" data-testid="trip-newsletter">
        <h3 className="text-2xl font-extrabold uppercase italic tracking-tight text-slate-900">Subscribe To Our Newsletter</h3>
        <p className="mt-4 text-slate-600">
          If you see something missing from our site, please reach out, send an email to{' '}
          <a href={`mailto:${email}`} className="font-semibold text-blue-800 underline hover:text-blue-900">{email}</a>{' '}
          and let us know what we can add, and we will do our very best to get it in place.
        </p>
      </section>

      <Footer />
    </div>
  );
};

export default BookYourTripPage;
